<template>
    <div class="rounded-xl border-iron-200 bg-white p-5 shadow-sm border">
        <div class="mb-4 flex items-start justify-between">
            <div>
                <h2 class="text-base font-semibold text-iron-900">
                    {{ heading.title }}
                </h2>
                <p class="mt-0.5 max-w-prose text-sm text-iron-500">
                    {{ heading.subtitle }}
                </p>
            </div>
            <Sparkles :size="18" class="mt-0.5 text-z-primary-500" />
        </div>

        <div v-if="!hasEventType" class="text-sm text-iron-500">
            {{ $t("checklist.hero.empty") }}
        </div>

        <Tabs
            v-else
            v-model="activeTab"
            class="flex flex-1 flex-col overflow-hidden"
        >
            <TabsList
                class="mb-4 p-1 bg-iron-100 rounded-lg inline-flex h-auto w-full"
            >
                <TabsTrigger
                    value="share"
                    class="gap-2 rounded-md py-2 px-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex-1 ring-0 ring-offset-0 data-[state=active]:ring-0"
                >
                    <Share :size="16" />
                    {{ $t("event_types.share_hub.tabs.share") }}
                </TabsTrigger>
                <TabsTrigger
                    value="embed"
                    class="gap-2 rounded-md py-2 px-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex-1 ring-0 ring-offset-0 data-[state=active]:ring-0"
                >
                    <Code :size="16" />
                    {{ $t("event_types.share_hub.tabs.embed") }}
                </TabsTrigger>
                <TabsTrigger
                    value="social"
                    class="gap-2 rounded-md py-2 px-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex-1 ring-0 ring-offset-0 data-[state=active]:ring-0"
                >
                    <Share2 :size="16" />
                    {{ $t("event_types.share_hub.tabs.social") }}
                </TabsTrigger>
                <TabsTrigger
                    value="qr"
                    class="gap-2 rounded-md py-2 px-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex-1 ring-0 ring-offset-0 data-[state=active]:ring-0"
                >
                    <QrCode :size="16" />
                    {{ $t("event_types.share_hub.tabs.qr_code") }}
                </TabsTrigger>
                <TabsTrigger
                    value="cms"
                    class="gap-2 rounded-md py-2 px-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex-1 ring-0 ring-offset-0 data-[state=active]:ring-0"
                >
                    <BookOpen :size="16" />
                    {{ $t("event_types.share_hub.tabs.cms") }}
                </TabsTrigger>
            </TabsList>

            <TabsContent value="share" class="mt-0">
                <ShareTab :booking-url="bookingUrl ?? ''" />
            </TabsContent>
            <TabsContent value="embed" class="mt-0">
                <EmbedTab
                    :token="embedToken ?? ''"
                    :event-type="{ isTeamEventType }"
                />
            </TabsContent>
            <TabsContent value="social" class="mt-0">
                <SocialTab
                    :booking-url="bookingUrl ?? ''"
                    :event-name="eventName ?? ''"
                />
            </TabsContent>
            <TabsContent v-if="eventTypeId !== null" value="qr" class="mt-0">
                <QrCodeTab :event-type-id="eventTypeId" />
            </TabsContent>
            <TabsContent value="cms" class="mt-0">
                <CmsGuidesTab :token="embedToken ?? ''" />
            </TabsContent>
        </Tabs>
    </div>
</template>

<script setup lang="ts">
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs-v2";
import {
    BookOpen,
    Code,
    QrCode,
    Share,
    Share2,
    Sparkles,
} from "lucide-vue-next";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useAnalytics } from "@/composables/useAnalytics";
import ShareTab from "@/Pages/Zaptime/App/EventTypes/components/ShareHub/tabs/ShareTab.vue";
import EmbedTab from "@/Pages/Zaptime/App/EventTypes/components/ShareHub/tabs/EmbedTab.vue";
import SocialTab from "@/Pages/Zaptime/App/EventTypes/components/ShareHub/tabs/SocialTab.vue";
import QrCodeTab from "@/Pages/Zaptime/App/EventTypes/components/ShareHub/tabs/QrCodeTab.vue";
import CmsGuidesTab from "@/Pages/Zaptime/App/EventTypes/components/ShareHub/tabs/CmsGuidesTab.vue";

interface Props {
    persona: "link" | "embed" | null;
    bookingUrl: string | null;
    embedToken: string | null;
    eventTypeId: number | null;
    eventName: string | null;
    isTeamEventType: boolean;
}

const props = defineProps<Props>();

const { t } = useI18n();
const analytics = useAnalytics();

const hasEventType = computed(
    () => Boolean(props.bookingUrl) || Boolean(props.embedToken),
);

const defaultTab = computed<"share" | "embed">(() =>
    props.persona === "embed" ? "embed" : "share",
);

const activeTab = ref<string>(defaultTab.value);

watch(activeTab, (next, prev) => {
    if (next === prev) {
        return;
    }
    analytics.trackEvent("checklist_hero_tab_changed", {
        from_tab: prev,
        to_tab: next,
        persona: props.persona,
    });
});

const heading = computed(() => {
    if (activeTab.value === "embed" || activeTab.value === "cms") {
        return {
            title: t("checklist.hero.embed.title"),
            subtitle: t("checklist.hero.embed.subtitle"),
        };
    }
    return {
        title: t("checklist.hero.link.title"),
        subtitle: t("checklist.hero.link.subtitle"),
    };
});
</script>
